export interface AppConfig {
  cognitoPool: string;
  cognitoClient: string;
  region: string;
  environment: string;
  release: string;
  sentryDsn?: string;
  sentryTracesSampleRate: number;
  sentrySampleRate: number;
  apiUrl: string;
  webSocketUrl: string;
  publicAppUrl?: string;
  hes: { cme: string; cme2100: string };
}

let cached: AppConfig | undefined;

// Parse a numeric VITE_* value, falling back when it's unset, empty, or not a
// number. Empty matters: an unset `${VAR}` interpolates to "" (not undefined),
// and Number("") is 0 — which would silently disable e.g. error capture.
function envNumber(value: string | undefined, fallback: number): number {
  if (value === undefined || value === "") {
    return fallback;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// Build the config from the Vite `.env` build-time env. The deploy bakes every
// VITE_* var into the bundle (see management-app/.env + .gitlab-ci.yml).
function build(): AppConfig {
  const env = import.meta.env;
  const cfg: AppConfig = {
    cognitoPool: env?.VITE_COGNITO_POOL_ID || "",
    cognitoClient: env?.VITE_COGNITO_CLIENT_ID || "",
    region: env?.VITE_REGION || "",
    environment: env?.VITE_ENVIRONMENT || "",
    release: env?.VITE_RELEASE || "dev",
    sentryDsn: env?.VITE_SENTRY_DSN,
    // Sentry sample rates are per-environment (prod records a small fraction;
    // dev review-apps run near-zero — Sentry is enabled there only so the trace
    // header propagation path is exercised in CI). Header propagation happens
    // regardless of the trace rate.
    sentryTracesSampleRate: envNumber(env?.VITE_SENTRY_TRACES_SAMPLE_RATE, 0),
    sentrySampleRate: envNumber(env?.VITE_SENTRY_SAMPLE_RATE, 1),
    apiUrl: env?.VITE_API_URL || window.localStorage.getItem("URL") || "",
    webSocketUrl: env?.VITE_WEB_SOCKET_URL || "",
    publicAppUrl: env?.VITE_PUBLIC_APP_URL,
    hes: {
      cme: env?.VITE_HES_CME || "",
      cme2100: env?.VITE_HES_CME2100 || "",
    },
  };
  const missing = (["cognitoPool", "cognitoClient", "apiUrl"] as const).filter(
    (k) => !cfg[k],
  );
  if (missing.length > 0 && env?.MODE !== "test") {
    console.error(
      `[config] missing required values: ${missing.join(", ")} — check the ` +
        "VITE_* build env.",
    );
  }
  return cfg;
}

/** The validated app config, sourced from Vite `.env` (build-time). Cached. */
export function appConfig(): AppConfig {
  if (!cached) {
    cached = build();
  }
  return cached;
}

export const getApiUrl = (): string => appConfig().apiUrl;

export const appUrl = (): string => appConfig().publicAppUrl ?? "";

export const getWebsocketBaseUrl = (): string => appConfig().webSocketUrl;

export const getCognitoConfig = () => {
  const cfg = appConfig();
  return {
    REGION: cfg.region,
    USER_POOL_ID: cfg.cognitoPool,
    APP_CLIENT_ID: cfg.cognitoClient,
  };
};

const config = {
  apiGateway: {
    REGION: appConfig().region,
    URL: getApiUrl(),
  },
  cognito: getCognitoConfig(),
};

export default config;
